import type { MatchState, PlayerState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { EntityId } from '../types';
import { refClarity } from './detect';

// Dive / simulation judging (§7.4) — pure. Judged the tick a player enters DIVE_FALL. Credibility blends
// timing (contact recency), commitment (an opponent genuinely lunging), and momentum (the player-authored
// fall direction vs the real push), scaled by the diver's reputation, then seen through referee clarity.

export type DiveVerdict = 'AWARDED' | 'PLAY_ON' | 'CAUGHT';

export interface DiveJudgment {
  verdict: DiveVerdict;
  timing01: number;
  commit01: number;
  momentum01: number;
  credibility: number;
  score: number;
  info: number;
  scoreSeen: number;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Commitment of the nearest opponent within 2 m: 1 in an active tackle/shove window, 0.6 in windup, else 0. */
function commit01(state: MatchState, diver: PlayerState, cfg: SimConfig): number {
  let nearest: PlayerState | undefined;
  let best = 2; // must be within 2 m
  for (const o of state.players) {
    if (o.team === diver.team || o.sentOff) continue;
    const d = Math.hypot(o.pos.x - diver.pos.x, o.pos.y - diver.pos.y);
    if (d <= best) {
      best = d;
      nearest = o;
    }
  }
  if (!nearest) return 0;
  const a = nearest.action;
  if (a === 'SHOVE' || a === 'PUNCH_ACTIVE') return 1;
  if (a === 'TACKLE_STAND') {
    const [lo, hi] = cfg.actions.tackle.STAND_WINDOW;
    return nearest.actionTicks >= lo && nearest.actionTicks <= hi ? 1 : 0.6;
  }
  if (a === 'TACKLE_SLIDE') {
    const [lo, hi] = cfg.actions.tackle.SLIDE_WINDOW;
    return nearest.actionTicks >= lo && nearest.actionTicks <= hi ? 1 : 0.6;
  }
  if (a === 'PUNCH_WINDUP') return 0.6;
  return 0;
}

export function judgeDive(state: MatchState, diverId: EntityId, cfg: SimConfig, infoOverride?: number): DiveJudgment {
  const R = cfg.referee;
  const p = state.players[diverId]!;
  const lc = p.lastContact;
  const oppContact = lc != null && state.players[lc.by] != null && state.players[lc.by]!.team !== p.team;
  const contactAge = oppContact ? state.tick - lc!.tick : Infinity;

  const timing01 = contactAge <= 5 ? 1 : clamp01(1 - (contactAge - 5) / 10);
  const commit = commit01(state, p, cfg);
  // momentum: how well the player-authored fall (facing) aligns with the real push (velocity + contact impulse)
  const ix = oppContact ? lc!.impulse.x : 0;
  const iy = oppContact ? lc!.impulse.y : 0;
  const bx = p.vel.x * 0.6 + ix * 0.4;
  const by = p.vel.y * 0.6 + iy * 0.4;
  const bl = Math.hypot(bx, by);
  const momentum01 = bl > 1e-6 ? clamp01((Math.cos(p.facing) * bx + Math.sin(p.facing) * by) / bl) : 0;

  const credibility = 0.4 * timing01 + 0.35 * commit + 0.25 * momentum01;
  const rep = state.discipline.perPlayer[diverId]!.diveReputation;
  const score = credibility * rep;
  const info = infoOverride ?? refClarity(state.referee.pos, state.referee.viewDir, p.pos, state, new Set<EntityId>([diverId]), cfg);
  const scoreSeen = lerp(0.5, score, info);

  let verdict: DiveVerdict;
  if ((!oppContact || contactAge > R.DIVE_WINDOW) && info >= R.I_MIN) verdict = 'CAUGHT';
  else if (scoreSeen >= R.T_AWARD) verdict = 'AWARDED';
  else if (scoreSeen < R.T_CAUGHT && info >= R.I_CATCH) verdict = 'CAUGHT';
  else verdict = 'PLAY_ON';

  return { verdict, timing01, commit01: commit, momentum01, credibility, score, info, scoreSeen };
}
